/**
 * LoadingScreen.helpers.js
 *
 * Pure functions, no React. Replaces the hardcoded Calories/Protein/
 * Water/BMI numbers that used to live directly in buildMockPlan() with
 * real calculations from the user's actual biometrics (basicInfo) and
 * activity level — everything here was already sitting unused in the
 * wizard's payload.
 *
 * This is still NOT the real UC-02 Rule Engine — it doesn't touch
 * medical conditions, allergens, pregnancy-adjusted targets (Screen 11's
 * trimester/gestational diabetes rules), or Nutrition Database filtering.
 * It only does the BMI/BMR/TDEE/macro math UC-02 step 3 describes, using
 * standard formulas, so the numbers are at least genuinely derived from
 * what the user entered instead of being the same for everyone.
 *
 * Self-contained by design (see the note in SummaryStep.helpers.js) — no
 * cross-folder imports.
 */

// Standard activity-multiplier table (Harris-Benedict/Mifflin-St Jeor
// convention). ActivityLevelStep's exact field name/shape wasn't
// available when this was written — resolveActivityMultiplier() below
// tries a few likely shapes and falls back to "moderate" if none match.
// Once ActivityLevelStep's real field is confirmed, tighten the lookup
// in resolveActivityMultiplier() to read it directly.
const ACTIVITY_MULTIPLIERS = {
  sedentary: 1.2,
  light: 1.375,
  lightly_active: 1.375,
  moderate: 1.55,
  moderately_active: 1.55,
  active: 1.725,
  very_active: 1.9,
  extra_active: 1.9,
};
const DEFAULT_ACTIVITY_MULTIPLIER = ACTIVITY_MULTIPLIERS.moderate;

function resolveActivityMultiplier(activity) {
  const candidate = activity?.activityLevel ?? activity?.level ?? activity?.id ?? activity;
  if (typeof candidate === "string" && ACTIVITY_MULTIPLIERS[candidate]) {
    return ACTIVITY_MULTIPLIERS[candidate];
  }
  return DEFAULT_ACTIVITY_MULTIPLIER;
}

// BMI = weight(kg) / height(m)^2
export function calculateBmi(weightKg, heightCm) {
  if (!weightKg || !heightCm) return null;
  const heightM = heightCm / 100;
  return weightKg / (heightM * heightM);
}

// Mifflin-St Jeor equation.
export function calculateBmr(weightKg, heightCm, age, gender) {
  if (!weightKg || !heightCm || !age) return null;
  const base = 10 * weightKg + 6.25 * heightCm - 5 * age;
  return gender === "female" ? base - 161 : base + 5;
}

export function calculateTdee(bmr, activity) {
  if (!bmr) return null;
  return bmr * resolveActivityMultiplier(activity);
}

// Adjusts TDEE toward the user's stated goal. Deliberately conservative
// (±15-20%, not an extreme deficit/surplus) — this is a rough target, not
// a clinical prescription, and matches typical rule-of-thumb guidance.
const GOAL_CALORIE_ADJUSTMENTS = {
  lose_weight: -0.18,
  gain_weight: 0.15,
  muscle_gain: 0.12,
  maintain_weight: 0,
  improve_fitness: 0,
  better_digestion: 0,
  healthy_lifestyle: 0,
  pregnancy_nutrition: 0.1, // extra energy needs, not a substitute for real trimester-based rules
};

export function calculateCalorieTarget(tdee, goal) {
  if (!tdee) return null;
  const adjustment = GOAL_CALORIE_ADJUSTMENTS[goal] ?? 0;
  return tdee * (1 + adjustment);
}

// Protein target in g/kg bodyweight — higher for muscle gain / weight
// loss (to preserve lean mass), moderate otherwise. Rough, not clinical.
const GOAL_PROTEIN_G_PER_KG = {
  lose_weight: 1.8,
  muscle_gain: 2.0,
  gain_weight: 1.8,
  maintain_weight: 1.4,
};
const DEFAULT_PROTEIN_G_PER_KG = 1.4;

export function calculateProteinTarget(weightKg, goal) {
  if (!weightKg) return null;
  const perKg = GOAL_PROTEIN_G_PER_KG[goal] ?? DEFAULT_PROTEIN_G_PER_KG;
  return weightKg * perKg;
}

// ~33ml per kg bodyweight/day is a common rough guideline.
export function calculateWaterTarget(weightKg) {
  if (!weightKg) return null;
  return (weightKg * 0.033);
}

/**
 * Builds the Health Summary block for the mock plan from real payload
 * data where available. Any field that can't be computed (missing
 * basicInfo, for instance) comes back as `null` rather than a fabricated
 * number — DietPlanScreen.js already renders a null/undefined summary
 * field as "—", so an incomplete profile shows honestly incomplete
 * numbers instead of confidently wrong ones.
 */
export function buildHealthSummary(payload) {
  const { weightKg, heightCm, age, gender } = payload?.basicInfo ?? {};
  const bmi = calculateBmi(weightKg, heightCm);
  const bmr = calculateBmr(weightKg, heightCm, age, gender);
  const tdee = calculateTdee(bmr, payload?.activity);
  const calories = calculateCalorieTarget(tdee, payload?.goal);
  const protein = calculateProteinTarget(weightKg, payload?.goal);
  const water = calculateWaterTarget(weightKg);

  return {
    calories: calories ? Math.round(calories / 10) * 10 : null,
    proteinG: protein ? Math.round(protein) : null,
    waterL: water ? Math.round(water * 10) / 10 : null,
    bmi: bmi ? Math.round(bmi * 10) / 10 : null,
    goal: payload?.goal ? String(payload.goal).replace(/_/g, " ") : "—",
  };
}
